import { useBaseWidget } from 'hooks/useBaseWidget';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import SwitchComponent from 'components/common/Switch/Switch';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/CommonComponents/CriticalAnalysisIntensity/criticalAnalysisIntensity.module.scss';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { LoaderContainer } from 'components/common';
import { useTrendsLogic } from 'components/widgets/Trends/Trends.logic';
import { useTrendsProvider } from 'components/widgets/Trends/Trends.provider';
import { Filters } from 'components/widgets/Trends/components/Filters/Filters';
import { TrendsGraph } from 'components/widgets/Trends/components/TrendsGraph/TrendsGraph';

export interface Dataset {
    title: string;
    seriesname: string;
    data: {
        text: string;
        displayValue: string;
        label: number;
        value: number;
        actualValue: number;
    }[];
    color: string;
    selectedByDefault: boolean;
}

export interface GraphData {
    dataset: Dataset[];
}

const title = 'Trend Analysis Per Selected Parameters';
const id = 'BaselineVsActual';
const componentKey = 'trends';

export const Trends = ({
    projectId,
    contractId,
    latestVersionId,
    globalFilterData,
    widgetId,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
}: IComponentProps) => {
    // Provider
    const { ui, api } = useTrendsProvider({
        widgetId,
        contractId,
        projectId,
        latestVersionId,
        globalFilterData,
        localFilters,
    });

    // Widget
    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
    });

    // Logic
    const { logic } = useTrendsLogic({
        ui,
        api,
        widgetId,
    });

    return (
        <WidgetWithTitle
            id={id}
            title={title}
            tooltip={
                ui.state.singleLine === 'Single Parameter (Actual value)'
                    ? api.crossVersionsTrends.data?.actual?.info
                    : api.crossVersionsTrends.data?.normalized?.info
            }
            titleComponents={[
                <div data-testid={'annotation-filter'} className={classes.annotationFilter}>
                    {ui.state.toggleLabels ? 'Hide Value Labels' : 'Show Value Labels'}
                    <SwitchComponent
                        onChange={() => ui.setters.setToggleLabels(!ui.state.toggleLabels)}
                        checked={ui.state.toggleLabels}
                    />
                </div>,
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    projectId={projectId}
                    contractId={contractId}
                    widgetId={widgetId}
                    route={route}
                    localFilters={ui.state}
                />,
                <ExportDropdown title={title} subTitle={subTitle} />,
            ]}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId && ui.pills.length > 0 ? (
                    <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={ui.pills} />
                ) : (
                    <Filters
                        key={'filters'}
                        options={logic.options}
                        handleSelectOptions={logic.handleSelectOptions}
                        handleSingle={logic.handleSingle}
                        sourceData={ui.state}
                    />
                ),
            ]}
        >
            {api.crossVersionsTrends.isLoading && !api.crossVersionsTrends.data && (
                <LoaderContainer>
                    <OverlayWithSpinner />
                </LoaderContainer>
            )}

            {!api.crossVersionsTrends.isLoading && api.crossVersionsTrends.data && ui.state.selectedOptions && (
                <TrendsGraph data={logic.graphData} toggleLabels={ui.state.toggleLabels} />
            )}
        </WidgetWithTitle>
    );
};
